import { motion } from 'framer-motion'

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'teal'
type BadgeSize = 'sm' | 'md' | 'lg'

interface BadgeProps {
  children: React.ReactNode
  variant?: BadgeVariant
  size?: BadgeSize
  icon?: React.ReactNode
  className?: string
  animate?: boolean
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-800',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
  teal: 'bg-[#46C0C6]/10 text-[#46C0C6]',
}

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
  lg: 'px-4 py-1.5 text-base',
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  icon,
  className = '',
  animate = false,
}: BadgeProps) {
  const Component = animate ? motion.span : 'span'
  const animationProps = animate
    ? {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        transition: { duration: 0.3 },
      }
    : {}

  return (
    <Component
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
      {...animationProps}
    >
      {icon}
      {children}
    </Component>
  )
}

// Skin Type Badge with specific styling
interface SkinTypeBadgeProps {
  skinType: 'dry' | 'normal' | 'combination' | 'oily'
  size?: BadgeSize
}

const skinTypeLabels: Record<string, string> = {
  dry: 'Dry',
  normal: 'Normal',
  combination: 'Combo',
  oily: 'Oily',
}

const skinTypeIcons: Record<string, string> = {
  dry: '💧',
  normal: '✨',
  combination: '🔄',
  oily: '💦',
}

export function SkinTypeBadge({ skinType, size = 'md' }: SkinTypeBadgeProps) {
  return (
    <Badge variant="teal" size={size} animate>
      <span>{skinTypeIcons[skinType]}</span>
      {skinTypeLabels[skinType]}
    </Badge>
  )
}

// Severity Badge
interface SeverityBadgeProps {
  severity: 'mild' | 'moderate' | 'severe'
  size?: BadgeSize
}

const severityVariants: Record<string, BadgeVariant> = {
  mild: 'success',
  moderate: 'warning',
  severe: 'error',
}

const severityLabels: Record<string, string> = {
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe',
}

export function SeverityBadge({ severity, size = 'sm' }: SeverityBadgeProps) {
  return (
    <Badge variant={severityVariants[severity]} size={size}>
      {severityLabels[severity]}
    </Badge>
  )
}
